import { useEffect, useMemo, useCallback, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Form,
  InputNumber,
  DatePicker,
  Select,
  Input,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  calculateLeaseEndDate,
  calculateProration,
  getApplicantRoleSuggestion,
  parseLocalDate,
} from "../../../easyapp.utils";
import { ADDENDA_OPTIONS, FEE_CATEGORIES, petTemplate, NATURAL_HAZARD_PROPERTY_ID } from "../../../easyapp.constants";

export const LeaseTermsForm = ({
  form,
  editMode,
  selectedAppGroup,
  plaidIncome,
  transactionCategories,
  unitRent,
  selectedPropertyFeeRecord,
}: any) => {
  const leaseTerm = Form.useWatch("leaseTerm", form);
  const moveInDate = Form.useWatch("moveInDate", form);
  const rent = Form.useWatch("rent", form);
  const fees = Form.useWatch("fees", form);
  const otherFees = Form.useWatch("otherFees", form);

  const initRef = useRef<boolean>(false);

  // Filter addenda options - only show Natural Hazard for the specific property with the creek
  const filteredAddendaOptions = useMemo(() => {
    const propertyId = selectedAppGroup?.desired_property_id;
    return ADDENDA_OPTIONS.filter(option => 
      option.value !== 'natural_hazard' || propertyId === NATURAL_HAZARD_PROPERTY_ID
    );
  }, [selectedAppGroup?.desired_property_id]);

  // Build combined list of occupant options (applicants + children from all applicants)
  const allOccupantOptions = useMemo(() => {
    const applicants = selectedAppGroup?.applicants || [];
    const options: { value: string; label: string; isChild: boolean; age: number; disabled?: boolean }[] = [];
    
    // Add adult applicants
    applicants.forEach((a: any) => {
      // Use parseLocalDate to avoid timezone-related age calculation errors
      const dobDate = a.dob ? parseLocalDate(a.dob) : null;
      const age = dobDate ? dayjs().diff(dayjs(dobDate), "year") : 0;
      options.push({
        value: a.application_id,
        label: `${a.first_name} ${a.last_name}`,
        isChild: false,
        age,
      });
    });

    // Add children from each applicant
    applicants.forEach((applicant: any) => {
      const children = applicant.children || applicant.minors || [];
      children.forEach((child: any, index: number) => {
        if (!child.first_name && !child.last_name) return; // Skip empty children
        const firstName = child.first_name || child.firstName || "";
        const lastName = child.last_name || child.lastName || "";
        if (!firstName && !lastName) return;
        
        const childId = `child-${applicant.application_id}-${index}`;
        const dob = child.dob || child.dateOfBirth;
        // Use parseLocalDate to avoid timezone-related age calculation errors
        const dobDate = dob ? parseLocalDate(dob) : null;
        const age = dobDate ? dayjs().diff(dayjs(dobDate), "year") : 0;
        
        options.push({
          value: childId,
          label: `${firstName} ${lastName} (Minor)`,
          isChild: true,
          age,
        });
      });
    });

    return options;
  }, [selectedAppGroup?.applicants]);

  // Get all child IDs for auto-selection
  const allChildIds = useMemo(() => {
    return allOccupantOptions
      .filter(opt => opt.isChild)
      .map(opt => opt.value);
  }, [allOccupantOptions]);

  const initialFees = useMemo(() => {
    if (!transactionCategories.length) return [];

    const rentValue =
      selectedAppGroup?.leaseDraft?.fees?.find(
        (f: any) =>
          transactionCategories
            .find((c: any) => c.ID === f.feeType)
            ?.Name.toLowerCase() === "rent"
      )?.amount ??
      unitRent ??
      0;

    return Object.entries(FEE_CATEGORIES)
      .map(([key, { defaultAmount, frequency }]) => {
        const normalizedKey = String(key).toLowerCase();

        const category = transactionCategories.find(
          (c: any) => c.Name.toLowerCase() === normalizedKey
        );

        return category
          ? {
              feeType: category.ID,
              frequency,
              startDate: selectedAppGroup?.desired_move_in_date
                ? dayjs(selectedAppGroup.desired_move_in_date).startOf("day")
                : null,
              amount:
                normalizedKey === "rent"
                  ? rentValue
                  : normalizedKey === "garage - attached"
                  ? selectedPropertyFeeRecord?.GarageFee || 0
                  : normalizedKey === "insurance waiver"
                  ? selectedPropertyFeeRecord?.InsuranceWaiverFee || 0
                  : normalizedKey === "media package"
                  ? selectedPropertyFeeRecord?.MediaAutomationFee || 0
                  : normalizedKey === "storage space"
                  ? selectedPropertyFeeRecord?.StorageFee || 0
                  : normalizedKey === "pet fees & charges"
                  ? selectedPropertyFeeRecord?.PetMonthlyFee || 0
                  : normalizedKey === "carport"
                  ? selectedPropertyFeeRecord?.CarportUnderground || 0
                  : defaultAmount,
            }
          : null;
      })
      .filter(Boolean);
  }, [unitRent, transactionCategories, selectedAppGroup]);

  const calculateTotalProration = useCallback(
    (moveInDateVal: any, feesArray: any[], otherFeesArray: any[]) => {
      if (!moveInDateVal || !moveInDateVal.isValid()) return 0;

      const sourceFees =
        Array.isArray(feesArray) && feesArray.length ? feesArray : initialFees;
      const sourceOtherFees = Array.isArray(otherFeesArray)
        ? otherFeesArray
        : [];

      const allMonthlyFees = [
        ...sourceFees.filter((f: any) => f.frequency === "Monthly"),
        ...sourceOtherFees.filter((f: any) => f.frequency === "Monthly"),
      ];

      const total = allMonthlyFees.reduce((sum: number, fee: any) => {
        const feeLabel = fee.feeName || "Fee";
        const amount = Number(fee.amount || 0);

        if (amount <= 0) return sum;

        const proratedAmount =
          calculateProration(moveInDateVal, amount, feeLabel)?.amount || 0;
        return sum + proratedAmount;
      }, 0);

      return Math.round(total);
    },
    [initialFees]
  );

  useEffect(() => {
    const existingRent = form.getFieldValue("rent");
    if (initRef.current || existingRent !== undefined) return;

    const hasSavedDraft = !!selectedAppGroup?.leaseDraft;

    if (hasSavedDraft) {
      const savedRentFee = selectedAppGroup.leaseDraft.fees?.find(
        (f: any) => f.feeName?.toLowerCase() === "rent"
      );
      const savedRent = (savedRentFee?.amount || unitRent) ?? 0;

      form.setFieldsValue({
        ...selectedAppGroup.leaseDraft,
        // Always use the current unit number from the app group (it may have been changed)
        unit: selectedAppGroup.desired_unit_number,
        rent: savedRent,
        moveInDate: selectedAppGroup.leaseDraft.moveInDate
          ? dayjs(selectedAppGroup.leaseDraft.moveInDate).startOf("day")
          : null,
        moveOutDate: selectedAppGroup.leaseDraft.moveOutDate
          ? dayjs(selectedAppGroup.leaseDraft.moveOutDate).startOf("day")
          : null,
        fees:
          selectedAppGroup.leaseDraft.fees?.map((fee: any) => ({
            ...fee,
            startDate: fee.startDate ? dayjs(fee.startDate).startOf("day") : null,
          })) || [],
        otherFees:
          selectedAppGroup.leaseDraft.otherFees?.map((fee: any) => ({
            ...fee,
            startDate: fee.startDate ? dayjs(fee.startDate).startOf("day") : null,
          })) || [],
        proration: selectedAppGroup.leaseDraft.proration,
        leaseholders: [
          // Include leaseholders from the saved draft
          ...(selectedAppGroup.leaseDraft.leaseholders?.map((l: any) => l.id) || []),
          // Include any new applicants that were added after the draft was saved
          ...(selectedAppGroup?.applicants
            ?.filter((a: any) => 
              !selectedAppGroup.leaseDraft.leaseholders?.some((l: any) => l.id === a.application_id)
            )
            .map((a: any) => a.application_id) || []),
        ],
        occupants: [
          ...(selectedAppGroup.leaseDraft.occupants?.map((o: any) => o.id) || []),
          // Include any new applicants that were added after the draft was saved
          ...(selectedAppGroup?.applicants
            ?.filter((a: any) => 
              !selectedAppGroup.leaseDraft.occupants?.some((o: any) => o.id === a.application_id)
            )
            .map((a: any) => a.application_id) || []),
          // Include children that aren't already in the saved occupants
          ...allChildIds.filter(
            (childId: string) => 
              !selectedAppGroup.leaseDraft.occupants?.some((o: any) => o.id === childId)
          ),
        ],
        pets:
          selectedAppGroup?.leaseDraft?.pets.map((pet: any) => ({
            ...pet,
            rabies: pet.rabies ? dayjs(pet.rabies).startOf("day") : null,
          })) || [],
        // Filter saved addenda to only include options available for this property
        addenda: (selectedAppGroup.leaseDraft.addenda || []).filter(
          (addendum: string) => filteredAddendaOptions.some(opt => opt.value === addendum)
        ),
        addendaData: selectedAppGroup.leaseDraft?.addendaData || {},
        concessions: selectedAppGroup.leaseDraft.concessions || [],
        conditions: selectedAppGroup.leaseDraft.conditions || [],
      });
    } else if (transactionCategories.length && selectedAppGroup) {
      const initialMoveOut = calculateLeaseEndDate(
        selectedAppGroup?.desired_move_in_date,
        selectedAppGroup?.desired_lease_term
      );

      const moveIn = dayjs(selectedAppGroup?.desired_move_in_date).startOf("day");
      const initialProration =
        moveIn.isValid() && initialFees.length
          ? calculateTotalProration(moveIn, initialFees, [])
          : 0;

      form.setFieldsValue({
        unit: selectedAppGroup.desired_unit_number,
        leaseTerm: selectedAppGroup.desired_lease_term,
        rent: unitRent ?? 0,
        moveInDate: selectedAppGroup.desired_move_in_date
          ? dayjs(selectedAppGroup.desired_move_in_date).startOf("day")
          : null,
        moveOutDate: initialMoveOut ? dayjs(initialMoveOut).startOf("day") : null,
        fees: initialFees,
        otherFees: [],
        proration: initialProration,
        concessions: [],
        conditions: [],
        leaseholders:
          selectedAppGroup?.applicants?.map((a: any) => a.application_id) || [],
        occupants: [
          ...(selectedAppGroup?.applicants?.map((a: any) => a.application_id) || []),
          ...allChildIds,
        ],
        pets: Array.from(
          new Map(
            (
              selectedAppGroup?.applicants?.flatMap((applicant: any) =>
                (applicant.pets || []).map((pet: any) => ({
                  ...petTemplate,
                  ...pet,
                  owner_name: `${applicant.first_name} ${applicant.last_name}`,
                  owner_email: applicant.email,
                }))
              ) || []
            ).map((pet: any) => [pet.name?.trim().toLowerCase(), pet])
          ).values()
        ).slice(0, 2),
        addenda: filteredAddendaOptions.map((option) => option.value),
      });
    }

    initRef.current = true;
  }, [
    form,
    selectedAppGroup,
    unitRent,
    transactionCategories,
    initialFees,
    calculateTotalProration,
    filteredAddendaOptions,
    allChildIds,
  ]);

  useEffect(() => {
    if (!moveInDate?.isValid()) return;

    const hasSavedDraft = !!selectedAppGroup?.leaseDraft;

    if (initRef.current && hasSavedDraft) {
      const draftMoveInDate = selectedAppGroup.leaseDraft.moveInDate
        ? dayjs(selectedAppGroup.leaseDraft.moveInDate).startOf("day")
        : null;
      const draftLeaseTerm = selectedAppGroup.leaseDraft.leaseTerm;

      const moveInChanged = !moveInDate.isSame(draftMoveInDate, "day");
      const leaseTermChanged = leaseTerm !== draftLeaseTerm;

      if (!moveInChanged && !leaseTermChanged) {
        return;
      }
    }

    form.setFieldsValue({
      moveOutDate: leaseTerm
        ? calculateLeaseEndDate(moveInDate, leaseTerm)
        : null,
    });
  }, [moveInDate, leaseTerm, form, selectedAppGroup]);

  useEffect(() => {
    if (!moveInDate || !moveInDate.isValid()) {
      return;
    }

    const hasSavedDraft = !!selectedAppGroup?.leaseDraft;

    if (initRef.current && hasSavedDraft) {
      const draftMoveInDate = selectedAppGroup.leaseDraft.moveInDate
        ? dayjs(selectedAppGroup.leaseDraft.moveInDate).startOf("day")
        : null;

      const moveInChanged = !moveInDate.isSame(draftMoveInDate, "day");

      if (!moveInChanged && !fees && !otherFees) {
        return;
      }
    }

    const total = calculateTotalProration(moveInDate, fees, otherFees);
    form.setFieldsValue({ proration: total });
  }, [moveInDate, fees, otherFees, calculateTotalProration, form, selectedAppGroup]);

  useEffect(() => {
    if (!rent) return;

    const hasSavedDraft = !!selectedAppGroup?.leaseDraft;

    if (initRef.current && hasSavedDraft) {
      const draftRentFee = selectedAppGroup.leaseDraft.fees?.find(
        (f: any) => f.feeName?.toLowerCase() === "rent"
      );
      const draftRent = draftRentFee?.amount;

      if (rent === draftRent) {
        return;
      }
    }

    const feesArray = form.getFieldValue("fees") || [];
    const rentCategory = transactionCategories.find(
      (c: any) => c.Name.toLowerCase() === "rent"
    );

    if (!rentCategory) return;

    const updatedFees = feesArray.map((fee: any) =>
      fee.feeType === rentCategory.ID ? { ...fee, amount: rent } : fee
    );

    form.setFieldsValue({ fees: updatedFees });
  }, [rent, form, transactionCategories, selectedAppGroup]);

  return (
    <Card
      style={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        marginBottom: 16,
      }}
      title={<Typography.Title level={4}>Lease Terms</Typography.Title>}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Unit Number"
            name="unit"
            rules={[{ required: true, message: "Unit number is required" }]}
          >
            <Input style={{ width: "100%" }} disabled />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Lease Term (Months)"
            name="leaseTerm"
            rules={[{ required: true, message: "Lease term is required" }]}
          >
            <InputNumber min={6} max={18} step={1} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Move-In Date"
            name="moveInDate"
            rules={[{ required: true, message: "Move-in date is required" }]}
          >
            <DatePicker style={{ width: "100%" }} format="MMM DD, YYYY" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Move-Out Date"
            name="moveOutDate"
            rules={[{ required: true, message: "Move-out date is required" }]}
          >
            <DatePicker style={{ width: "100%" }} format="MMM DD, YYYY" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Monthly Rent"
            name="rent"
            rules={[
              { required: true, message: "Rent is required" },
              { type: "number", min: 0, message: "Rent must be positive" },
            ]}
          >
            <InputNumber prefix="$" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Proration"
            name="proration"
            rules={[{ required: false }]}
          >
            <InputNumber
              prefix="$"
              min={0}
              style={{ width: "100%" }}
              disabled
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item label="Addenda" name="addenda">
            <Select
              mode="multiple"
              placeholder="Select addenda to include"
              options={filteredAddendaOptions}
              disabled
            />
          </Form.Item>
        </Col>
        <Form.Item name="pets" hidden></Form.Item>
        <Col xs={24}>
          <Form.Item
            label="Leaseholders (Signers)"
            name="leaseholders"
            rules={[
              { required: true, message: "At least one leaseholder required" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select leaseholders"
              optionRender={(option: any) => {
                const applicant = selectedAppGroup?.applicants?.find(
                  (a: any) => a.application_id === option.value
                );
                const index = selectedAppGroup?.applicants?.findIndex(
                  (a: any) => a.application_id === option.value
                );
                const { role, reason } = getApplicantRoleSuggestion(
                  applicant,
                  index || 0,
                  rent ?? unitRent,
                  plaidIncome?.data
                );
                return role === "Leaseholder" ? (
                  <Tooltip title={reason}>
                    <Space>
                      <span>{option.label}</span>
                      <Tag color="blue">Suggested: Leaseholder</Tag>
                    </Space>
                  </Tooltip>
                ) : (
                  <span>{option.label}</span>
                );
              }}
              options={selectedAppGroup?.applicants?.map((a: any) => ({
                value: a.application_id,
                label: `${a.first_name} ${a.last_name}`,
              }))}
              disabled
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item label="Occupants" name="occupants" required>
            <Select
              mode="multiple"
              placeholder="Select occupants"
              optionRender={(option: any) => {
                const occupantOpt = allOccupantOptions.find(o => o.value === option.value);
                
                // If it's a child, show with Minor tag
                if (occupantOpt?.isChild) {
                  return (
                    <Space>
                      <span>{option.label}</span>
                      <Tag color="orange">Minor ({occupantOpt.age} yrs)</Tag>
                    </Space>
                  );
                }
                
                // For adult applicants, show role suggestion
                const applicant = selectedAppGroup?.applicants?.find(
                  (a: any) => a.application_id === option.value
                );
                const index = selectedAppGroup?.applicants?.findIndex(
                  (a: any) => a.application_id === option.value
                );
                const { role, reason } = getApplicantRoleSuggestion(
                  applicant,
                  index || 0,
                  rent ?? unitRent,
                  plaidIncome?.data
                );
                return role === "Occupant" ? (
                  <Tooltip title={reason}>
                    <Space>
                      <span>{option.label}</span>
                      <Tag color="blue">Suggested: Occupant</Tag>
                    </Space>
                  </Tooltip>
                ) : (
                  <span>{option.label}</span>
                );
              }}
              options={allOccupantOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
                disabled: !opt.isChild && form
                  .getFieldValue("leaseholders")
                  ?.includes(opt.value),
              }))}
              disabled
            />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};